
import React, { useState, useRef } from 'react';
import './VendorForm.css';
import axios from '../../redux/axiosInstance';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

const VendorOnboarding = () => {
  const apiUrl = process.env.REACT_APP_API;
  const fileInputRef = useRef(null);
  const user = useSelector((state) => state.user?.user || state.admin?.admin || {});
  const actorName = user.name || user.email || 'Admin';
  const actorEmail = user.email || '';

  const initialFormState = {
    organizationName: '',
    legalEntityName: '',
    vendorType: '',
    businessCategory: '',
    registrationNumber: '',
    gstTaxId: '',
    registeredAddress: '',
    country: '',
    state: '',
    city: '',
    primaryContactName: '',
    contactEmail: '',
    contactPhone: '',
    website: '',
    productsServices: '',
    bankName: '',
    accountNumber: '',
    ifscCode: '',
    branchName: '',
    status: 'Pending',
  };

  const initialComplianceState = {
    registeredBusiness: false,
    taxCompliant: false,
    isoCertified: false,
    gdprCompliant: false,
    ndaSigned: false,
    insuranceAvailable: false,
  };

  const [formData, setFormData] = useState(initialFormState);
  const [compliance, setCompliance] = useState(initialComplianceState);
  const [documents, setDocuments] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [activeSection, setActiveSection] = useState(0);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleComplianceChange = (e) => {
    setCompliance({ ...compliance, [e.target.name]: e.target.checked });
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setDocuments((prev) => [...prev, ...newFiles]);
    // Reset file input so the same file can be re-added if removed
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (index) => {
    setDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  const requiredFieldsBySection = [
    { keys: ['organizationName', 'vendorType', 'country', 'state', 'city'], label: 'Organization' },
    { keys: ['primaryContactName', 'contactEmail', 'contactPhone'], label: 'Contact' },
    { keys: [], label: 'Bank Details' },
    { keys: [], label: 'Compliance' },
  ];

  const validateForm = () => {
    for (let i = 0; i < requiredFieldsBySection.length; i++) {
      const { keys, label } = requiredFieldsBySection[i];
      const hasMissingField = keys.some((key) => !formData[key]?.trim());
      if (hasMissingField) {
        setActiveSection(i);
        toast.error(`Please fill in all required fields in the ${label} section.`);
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);

    try {
      const payload = new FormData();

      // Append all text fields
      Object.entries(formData).forEach(([key, value]) => {
        payload.append(key, value);
      });
      payload.append('createdBy', actorName);
      payload.append('createdByEmail', actorEmail);

      // Append compliance as JSON string
      payload.append('compliance', JSON.stringify(compliance));

      // Append document files
      documents.forEach((file) => {
        payload.append('documents', file);
      });

      const response = await axios.post(`${apiUrl}/vendor-add`, payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      console.log(response.data);
      setSubmitSuccess(true);
      toast.success('Vendor onboarded successfully!');

      // Reset form
      setFormData(initialFormState);
      setCompliance(initialComplianceState);
      setDocuments([]);

      setTimeout(() => setSubmitSuccess(false), 4000);
    } catch (error) {
      console.error('There was an error adding the vendor!', error);
      toast.error('Error adding vendor. Please check all required fields.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const sections = [
    { id: 'org', label: 'Organization', icon: '🏢' },
    { id: 'contact', label: 'Contact', icon: '👤' },
    { id: 'bank', label: 'Bank Details', icon: '🏦' },
    { id: 'compliance', label: 'Compliance', icon: '✅' },
  ];

  const vendorTypes = ['Supplier', 'Contractor', 'Consultant', 'Service Provider', 'Distributor', 'Other'];
  const businessCategories = [
    'IT & Software', 'Manufacturing', 'Logistics', 'Healthcare', 'Construction',
    'Financial Services', 'Retail', 'Consulting', 'Energy', 'Telecommunications', 'Other'
  ];

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  return (
    <div className="vo-page">
      <div className="vo-container">
        {/* Header */}
        <div className="vo-header">
          <div className="vo-header-content" >
            <div className="vo-header-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <line x1="19" y1="8" x2="19" y2="14" />
                <line x1="22" y1="11" x2="16" y2="11" />
              </svg>
            </div>
            <div>
              <h1 className="vo-title">Vendor Onboarding</h1>
              <p className="vo-subtitle">Register a new vendor to the procurement system</p>
            </div>
          </div>
        </div>

        {/* Section Navigation */}
        <div className="vo-section-nav">
          {sections.map((section, index) => (
            <button
              key={section.id}
              type="button"
              className={`vo-section-tab ${activeSection === index ? 'active' : ''} ${index < activeSection ? 'completed' : ''}`}
              onClick={() => setActiveSection(index)}
            >
              <span className="vo-tab-step">{index < activeSection ? '✓' : index + 1}</span>
              <span className="vo-tab-label">{section.label}</span>
            </button>
          ))}
        </div>

        {/* Success Toast */}
        {submitSuccess && (
          <div className="vo-success-toast">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
            <span>Vendor onboarded successfully!</span>
          </div>
        )}

        <form className="vo-form" onSubmit={handleSubmit} noValidate>

          {/* Section 1: Organization Details */}
          <div className={`vo-section ${activeSection === 0 ? 'visible' : ''}`}>
            <div className="vo-section-header">
              <h2>Organization Details</h2>
              <p>Basic information about the vendor organization</p>
            </div>
            <div className="vo-grid">
              <div className="vo-field">
                <label htmlFor="organizationName">Organization Name <span className="vo-required">*</span></label>
                <input id="organizationName" type="text" name="organizationName" value={formData.organizationName} onChange={handleChange} placeholder="Enter organization name" required />
              </div>
              <div className="vo-field">
                <label htmlFor="legalEntityName">Legal Entity Name</label>
                <input id="legalEntityName" type="text" name="legalEntityName" value={formData.legalEntityName} onChange={handleChange} placeholder="Legal entity name" />
              </div>
              <div className="vo-field">
                <label htmlFor="vendorType">Vendor Type <span className="vo-required">*</span></label>
                <select id="vendorType" name="vendorType" value={formData.vendorType} onChange={handleChange} required>
                  <option value="">Select vendor type</option>
                  {vendorTypes.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              <div className="vo-field">
                <label htmlFor="businessCategory">Business Category</label>
                <select id="businessCategory" name="businessCategory" value={formData.businessCategory} onChange={handleChange}>
                  <option value="">Select business category</option>
                  {businessCategories.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div className="vo-field">
                <label htmlFor="registrationNumber">Registration Number</label>
                <input id="registrationNumber" type="text" name="registrationNumber" value={formData.registrationNumber} onChange={handleChange} placeholder="Business registration number" />
              </div>
              <div className="vo-field">
                <label htmlFor="gstTaxId">GST / Tax ID</label>
                <input id="gstTaxId" type="text" name="gstTaxId" value={formData.gstTaxId} onChange={handleChange} placeholder="GST or Tax ID" />
              </div>
              <div className="vo-field vo-field-full">
                <label htmlFor="registeredAddress">Registered Address</label>
                <textarea id="registeredAddress" name="registeredAddress" value={formData.registeredAddress} onChange={handleChange} placeholder="Full registered address" rows="2" />
              </div>
              <div className="vo-field">
                <label htmlFor="country">Country <span className="vo-required">*</span></label>
                <input id="country" type="text" name="country" value={formData.country} onChange={handleChange} placeholder="Country" required />
              </div>
              <div className="vo-field">
                <label htmlFor="state">State <span className="vo-required">*</span></label>
                <input id="state" type="text" name="state" value={formData.state} onChange={handleChange} placeholder="State / Province" required />
              </div>
              <div className="vo-field">
                <label htmlFor="city">City <span className="vo-required">*</span></label>
                <input id="city" type="text" name="city" value={formData.city} onChange={handleChange} placeholder="City" required />
              </div>
              <div className="vo-field">
                <label htmlFor="status">Status</label>
                <select id="status" name="status" value={formData.status} onChange={handleChange}>
                  <option value="Pending">Pending</option>
                  <option value="Approved">Approved</option>
                  <option value="Rejected">Rejected</option>
                </select>
              </div>
            </div>

            {/* Document Upload */}
            <div className="vo-upload-section">
              <h3 className="vo-upload-title">Vendor Documents</h3>
              <p className="vo-upload-desc">Upload registration certificates, tax documents, GST certificates, etc.</p>
              <div
                className="vo-dropzone"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('dragover'); }}
                onDragLeave={(e) => { e.currentTarget.classList.remove('dragover'); }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove('dragover');
                  const droppedFiles = Array.from(e.dataTransfer.files);
                  setDocuments((prev) => [...prev, ...droppedFiles]);
                }}
              >
                <svg className="vo-dropzone-icon" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                <span className="vo-dropzone-text">Click or drag files here to upload</span>
                <span className="vo-dropzone-hint">PDF, DOC, JPG, PNG — Max 10MB each</span>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
              </div>

              {documents.length > 0 && (
                <div className="vo-file-list">
                  {documents.map((file, index) => (
                    <div key={index} className="vo-file-item">
                      <div className="vo-file-info">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                        </svg>
                        <span className="vo-file-name">{file.name}</span>
                        <span className="vo-file-size">{formatFileSize(file.size)}</span>
                      </div>
                      <button type="button" className="vo-file-remove" onClick={() => removeFile(index)} title="Remove file">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="vo-section-actions">
              <span></span>
              <button type="button" className="vo-btn vo-btn-next" onClick={() => setActiveSection(1)}>
                Continue to Contact
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
              </button>
            </div>
          </div>

          {/* Section 2: Contact Details */}
          <div className={`vo-section ${activeSection === 1 ? 'visible' : ''}`}>
            <div className="vo-section-header">
              <h2>Contact Information</h2>
              <p>Primary contact and communication details</p>
            </div>
            <div className="vo-grid">
              <div className="vo-field">
                <label htmlFor="primaryContactName">Primary Contact Name <span className="vo-required">*</span></label>
                <input id="primaryContactName" type="text" name="primaryContactName" value={formData.primaryContactName} onChange={handleChange} placeholder="Full name" required />
              </div>
              <div className="vo-field">
                <label htmlFor="contactEmail">Contact Email <span className="vo-required">*</span></label>
                <input id="contactEmail" type="email" name="contactEmail" value={formData.contactEmail} onChange={handleChange} placeholder="email@company.com" required />
              </div>
              <div className="vo-field">
                <label htmlFor="contactPhone">Contact Phone <span className="vo-required">*</span></label>
                <input id="contactPhone" type="text" name="contactPhone" value={formData.contactPhone} onChange={handleChange} placeholder="+91 XXXXX XXXXX" required />
              </div>
              <div className="vo-field">
                <label htmlFor="website">Website</label>
                <input id="website" type="url" name="website" value={formData.website} onChange={handleChange} placeholder="https://www.company.com" />
              </div>
              <div className="vo-field vo-field-full">
                <label htmlFor="productsServices">Products / Services Offered</label>
                <textarea id="productsServices" name="productsServices" value={formData.productsServices} onChange={handleChange} placeholder="Describe the products or services this vendor offers..." rows="3" />
              </div>
            </div>
            <div className="vo-section-actions">
              <button type="button" className="vo-btn vo-btn-back" onClick={() => setActiveSection(0)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
                Back
              </button>
              <button type="button" className="vo-btn vo-btn-next" onClick={() => setActiveSection(2)}>
                Continue to Bank & Docs
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
              </button>
            </div>
          </div>

          {/* Section 3: Bank Details & Documents */}
          <div className={`vo-section ${activeSection === 2 ? 'visible' : ''}`}>
            <div className="vo-section-header">
              <h2>Bank Details</h2>
              <p>Financial information</p>
            </div>
            <div className="vo-grid">
              <div className="vo-field">
                <label htmlFor="bankName">Bank Name</label>
                <input id="bankName" type="text" name="bankName" value={formData.bankName} onChange={handleChange} placeholder="Bank name" />
              </div>
              <div className="vo-field">
                <label htmlFor="accountNumber">Account Number</label>
                <input id="accountNumber" type="text" name="accountNumber" value={formData.accountNumber} onChange={handleChange} placeholder="Account number" />
              </div>
              <div className="vo-field">
                <label htmlFor="ifscCode">IFSC Code</label>
                <input id="ifscCode" type="text" name="ifscCode" value={formData.ifscCode} onChange={handleChange} placeholder="IFSC code" />
              </div>
              <div className="vo-field">
                <label htmlFor="branchName">Branch Name</label>
                <input id="branchName" type="text" name="branchName" value={formData.branchName} onChange={handleChange} placeholder="Branch name" />
              </div>
            </div>


            <div className="vo-section-actions">
              <button type="button" className="vo-btn vo-btn-back" onClick={() => setActiveSection(1)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
                Back
              </button>
              <button type="button" className="vo-btn vo-btn-next" onClick={() => setActiveSection(3)}>
                Continue to Compliance
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
              </button>
            </div>
          </div>

          {/* Section 4: Compliance */}
          <div className={`vo-section ${activeSection === 3 ? 'visible' : ''}`}>
            <div className="vo-section-header">
              <h2>Compliance & Certifications</h2>
              <p>Verify the vendor's compliance and certification status</p>
            </div>

            <div className="vo-compliance-grid">
              {[
                { key: 'registeredBusiness', label: 'Registered Business', desc: 'Vendor is a registered business entity' },
                { key: 'taxCompliant', label: 'Tax Compliant', desc: 'All tax obligations are up to date' },
                { key: 'isoCertified', label: 'ISO Certified', desc: 'Holds relevant ISO certifications' },
                { key: 'gdprCompliant', label: 'GDPR Compliant', desc: 'Compliant with data protection regulations' },
                { key: 'ndaSigned', label: 'NDA Signed', desc: 'Non-Disclosure Agreement has been signed' },
                { key: 'insuranceAvailable', label: 'Insurance Available', desc: 'Business insurance coverage is active' },
              ].map((item) => (
                <label key={item.key} className={`vo-compliance-card ${compliance[item.key] ? 'checked' : ''}`}>
                  <div className="vo-compliance-check">
                    <input
                      type="checkbox"
                      name={item.key}
                      checked={compliance[item.key]}
                      onChange={handleComplianceChange}
                    />
                    <span className="vo-checkmark">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </span>
                  </div>
                  <div className="vo-compliance-text">
                    <span className="vo-compliance-label">{item.label}</span>
                    <span className="vo-compliance-desc">{item.desc}</span>
                  </div>
                </label>
              ))}
            </div>

            <div className="vo-section-actions">
              <button type="button" className="vo-btn vo-btn-back" onClick={() => setActiveSection(2)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
                Back
              </button>
              <button
                type="submit"
                className={`vo-btn vo-btn-submit ${isSubmitting ? 'submitting' : ''}`}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="vo-spinner"></span>
                    Submitting...
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                      <polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                    Submit Vendor
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VendorOnboarding;
